<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';

  export let fullScreen = false;
  export let closeOnEscape = true;
  export let closeOnBackdrop = false;

  const dispatch = createEventDispatcher();

  function requestClose() {
    dispatch('close');
  }

  function handleKeydown(e: KeyboardEvent) {
    if (closeOnEscape && e.key === 'Escape') {
      e.preventDefault();
      requestClose();
    }
  }

  function handleBackdrop() {
    if (closeOnBackdrop) requestClose();
  }

  let previousOverflow = '';

  onMount(() => {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  });

  onDestroy(() => {
    document.body.style.overflow = previousOverflow;
  });
</script>

<svelte:window on:keydown={handleKeydown} />

<div
  class="flow-modal-backdrop"
  class:full-screen={fullScreen}
  role="presentation"
  on:click|self={handleBackdrop}
  on:keydown|self={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBackdrop(); }}
>
  <div
    class="flow-modal"
    class:full-screen={fullScreen}
    role="dialog"
    aria-modal="true"
    tabindex="-1"
  >
    <button
      type="button"
      class="flow-modal-close"
      aria-label="Close"
      on:click={requestClose}
    >
      <i class="close icon"></i>
    </button>
    <div class="flow-modal-content">
      <slot />
    </div>
  </div>
</div>

<style>
  .flow-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .flow-modal-backdrop.full-screen {
    background: transparent;
  }

  .flow-modal {
    position: relative;
    width: 90vw;
    height: 90vh;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  }

  .flow-modal.full-screen {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }

  .flow-modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: #333;
    font-size: 1.25rem;
    line-height: 1;
    z-index: 10;
    transition: background 0.15s ease;
  }

  .flow-modal-close:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  .flow-modal-close:focus-visible {
    outline: 2px solid var(--primary, #056cf2);
    outline-offset: 2px;
  }

  .flow-modal-close :global(i.icon) {
    margin: 0;
  }

  .flow-modal-content {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-y: auto;
  }

  /* Neutralise viewport-fixed positioning for child flows so content
     centers within the modal panel rather than the whole screen. */
  .flow-modal-content :global(.center-fixed),
  .flow-modal-content :global(.center-x-fixed) {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .flow-modal-content :global(.center-x-fixed) {
    top: 35%;
    transform: translate(-50%, -35%);
  }

  @media (max-width: 768px) {
    .flow-modal {
      width: 100vw;
      height: 100vh;
      border-radius: 0;
    }
  }
</style>
